import { ActionIcon, Button, Text, Tooltip } from '@mantine/core'
import dayjs from 'dayjs'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { useCurrentCompanyContext } from 'src/context/CurrentCompanyContext'
import IconCopy from 'src/icons/IconCopy'
import IconLink from 'src/icons/IconLink'
import { clearDriverToken } from 'src/lib/driverAuth'
import {
  buildAuth0Url,
  buildDatadogLogsUrl,
  buildInngestUrl,
  buildSentryUrl,
} from 'src/lib/observability'
import { buildApplicationUrl } from 'src/lib/url.utils'

interface DebugTabProps {
  applicantId: string
  applicant: {
    applicantId: string
    firstName?: string
    lastName?: string
    phone?: string
    createdAt?: string
    lastActionDate?: string
    application?: {
      applicationId?: string
      trackingLink?: {
        trackingLinkId?: string
        linkType?: string
        linkName?: string
      }
    }
    jobListing?: {
      jobListingId?: string
      title?: string
    }
    recruiter?: {
      employeeId?: string
      firstName?: string
      lastName?: string
    }
    memberEmployees?: Array<{
      employeeId?: string
      firstName?: string
      lastName?: string
    }>
  } | null
}

const CopyField = ({
  label,
  value,
  name,
  datadogUrl,
}: {
  label: string
  value: string
  name?: string
  datadogUrl?: string
}) => {
  const handleCopy = () => {
    navigator?.clipboard?.writeText?.(value)
    toast(`${label} copied to clipboard`, 'success')
  }

  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex flex-col">
        <Text size="sm" fw={500}>
          {label}
        </Text>
        {name && (
          <Text size="xs" c="dimmed">
            {name}
          </Text>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Text size="sm" className="font-mono text-gray-600">
          {value || '-'}
        </Text>
        {value && (
          <Tooltip label="Copy" withArrow>
            <ActionIcon variant="subtle" size="sm" color="gray" onClick={handleCopy}>
              <IconCopy className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-500" />
            </ActionIcon>
          </Tooltip>
        )}
        {datadogUrl && (
          <a
            href={datadogUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 rounded border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-600 transition-colors hover:border-blue-300 hover:bg-blue-100"
          >
            <IconLink className="h-3 w-3 fill-none stroke-current" />
            Datadog
          </a>
        )}
      </div>
    </div>
  )
}

const DebugTab = ({ applicantId, applicant }: DebugTabProps) => {
  const currentCompany = useCurrentCompanyContext()
  const companyId = currentCompany?.companyId

  const sentryUrl = buildSentryUrl(applicantId)
  const inngestUrl = buildInngestUrl()
  const auth0Url = applicant?.phone ? buildAuth0Url(applicant.phone) : null

  return (
    <div className="flex h-[90%] flex-col gap-6 overflow-auto pb-6">
      {/* IDs & Datadog Section */}
      <div className="rounded-md border border-gray-200 p-4">
        <Text fw={600} mb="md">IDs & Datadog</Text>
        <div className="flex flex-col divide-y divide-gray-100">
          <CopyField
            label="Applicant ID"
            value={applicantId}
            name={
              applicant?.firstName
                ? `${applicant.firstName} ${applicant.lastName || ''}`
                : undefined
            }
            datadogUrl={buildDatadogLogsUrl({ applicantId })}
          />
          {companyId && (
            <CopyField
              label="Company ID"
              value={companyId}
              name={currentCompany?.companyName}
              datadogUrl={buildDatadogLogsUrl({ applicantId, companyId })}
            />
          )}
          {applicant?.application?.applicationId && (
            <CopyField
              label="Application ID"
              value={applicant.application.applicationId}
              datadogUrl={buildDatadogLogsUrl({ applicantId, applicationId: applicant.application.applicationId })}
            />
          )}
          {applicant?.application?.applicationId && (
            <CopyField
              label="Driver Application Link"
              value={buildApplicationUrl(applicant.application.applicationId)}
            />
          )}
          {applicant?.jobListing?.jobListingId && (
            <CopyField
              label="Job Listing ID"
              value={applicant.jobListing.jobListingId}
              name={applicant.jobListing.title}
              datadogUrl={buildDatadogLogsUrl({ applicantId, jobListingId: applicant.jobListing.jobListingId })}
            />
          )}
          {applicant?.recruiter?.employeeId && (
            <CopyField
              label="Lead Employee ID"
              value={applicant.recruiter.employeeId}
              name={`${applicant.recruiter.firstName || ''} ${applicant.recruiter.lastName || ''}`.trim()}
              datadogUrl={buildDatadogLogsUrl({ applicantId, employeeId: applicant.recruiter.employeeId })}
            />
          )}
          {applicant?.memberEmployees?.map((member) => (
            <CopyField
              key={member.employeeId}
              label="Member Employee ID"
              value={member.employeeId || ''}
              name={`${member.firstName || ''} ${member.lastName || ''}`.trim()}
              datadogUrl={member.employeeId ? buildDatadogLogsUrl({ applicantId, employeeId: member.employeeId }) : undefined}
            />
          ))}
        </div>
        {(applicant?.createdAt || applicant?.lastActionDate || applicant?.application?.trackingLink) && (
          <div className="mt-3 flex flex-wrap gap-4 text-xs text-gray-500">
            {applicant?.application?.trackingLink && (
              <span>
                Source:{' '}
                <span className="font-medium text-gray-700">
                  {applicant.application.trackingLink.linkName ||
                    applicant.application.trackingLink.linkType ||
                    'Unknown'}
                </span>
              </span>
            )}
            {applicant?.createdAt && (
              <span>
                Created: {dayjs(applicant.createdAt).format('MMM D, YYYY h:mm A')}
              </span>
            )}
            {applicant?.lastActionDate && (
              <span>
                Last Action:{' '}
                {dayjs(applicant.lastActionDate).format('MMM D, YYYY h:mm A')}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Integrated Tools Section */}
      <div className="rounded-md border border-gray-200 p-4">
        <Text fw={600} mb="sm">Integrated Tools</Text>
        <div className="flex flex-wrap gap-3">
          <a
            href={sentryUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 transition-colors hover:border-red-300 hover:bg-red-50"
          >
            <Text size="sm" fw={500}>Sentry Issues</Text>
            <IconLink className="h-3.5 w-3.5 fill-none stroke-gray-400" />
          </a>
          <a
            href={inngestUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 transition-colors hover:border-purple-300 hover:bg-purple-50"
          >
            <Text size="sm" fw={500}>Background Jobs</Text>
            <IconLink className="h-3.5 w-3.5 fill-none stroke-gray-400" />
          </a>
          {auth0Url && (
            <a
              href={auth0Url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 transition-colors hover:border-orange-300 hover:bg-orange-50"
            >
              <Text size="sm" fw={500}>Auth0 User</Text>
              <IconLink className="h-3.5 w-3.5 fill-none stroke-gray-400" />
            </a>
          )}
        </div>
      </div>

      {/* Local Session Section */}
      <div className="rounded-md border border-gray-200 p-4">
        <Text fw={600} mb="xs">
          Local session
        </Text>
        <Text size="xs" c="dimmed" mb="sm">
          Removes this browser&apos;s driver token so an admin (Auth0) session
          isn&apos;t shadowed by a leftover driver login on /driver-application.
        </Text>
        <Button
          variant="default"
          size="xs"
          onClick={() => {
            clearDriverToken()
            toast('Driver session cleared', 'success')
          }}
        >
          Clear driver session
        </Button>
      </div>
    </div>
  )
}

export default DebugTab
